import * as Rx from 'rxjs';
import { last, map, switchMap } from 'rxjs/operators';
import type { IrregularProduct } from 'scenes/Producer/Product/MyProducts/constants';
import { isLegalPersonAccount } from 'v2/helpers/isLegalPersonAccount';
import type { IApiResponse } from 'v2/interfaces/api';
import type {
  CreateOfferPayload,
  GetProductOffersParams,
  IBasic,
  IContentDelivery,
  IProduct,
  PlanChangeConfig,
  ProductOffer,
  SetPlanChangeConfigPayload,
  SubscriptionPlan,
  UpdateOfferPayload
} from 'v2/interfaces/product';
import { userStore } from 'v2/store/User';

import type { ApiService } from './api';
import apiService from './api';
import { apiServiceProxy } from './api-proxy';

class ProductService {
  private product$ = new Rx.ReplaySubject<IProduct>(1);

  constructor(private apiService: ApiService) {}

  public get(): Rx.Observable<IProduct> {
    return this.product$;
  }

  public load(id: number): void {
    this.apiService.get(`newproduct/${id}`).subscribe((product: IProduct) => this.product$.next(product));
  }

  //TODO rever
  public set(product: Partial<IProduct>): void {
    this.product$.pipe(last()).subscribe((currentState: IProduct) => {
      this.product$.next({ ...currentState, ...product });
    });
  }

  public saveGeneralBasic(): void {
    this.product$
      .pipe(
        map((product: IProduct): IBasic => {
          const basic: IBasic = {
            id: product.id,
            title: product.title,
            categories: product.categories,
            description: product.description,
            type: product.type,
            image: product.image
          };
          return basic;
        }),
        switchMap(model => this.apiService.put(`newproduct/${model.id}/general/basic`, model))
      )
      .subscribe();
  }

  public savePins(productId: string, pins: string[]) {
    return this.apiService.post(`/products/${productId}/deliverymethods/createbatch`, { pins });
  }

  public activateConversionTools() {
    return this.apiService.post('/products/activate-conversion-tools', {});
  }

  public getCoproducers(productId: number) {
    return this.apiService.get(`/products/${productId}/coproducers`).pipe(map(res => res.data));
  }

  public updateCoproducers(productId: number, coproducers: any) {
    return this.apiService.put(`/products/${productId}/coproducers`, {
      coproducers
    });
  }

  public getProductVariations(parentId: number): Promise<IProduct[]> {
    return this.apiService
      .get(`/products?parent_id=${parentId}&per_page=1000`)
      .pipe(map(res => res.data))
      .toPromise();
  }

  public getIrregularProductVariations(parentId: number): Promise<IrregularProduct[]> {
    return this.apiService
      .get(`/products/irregular?parent_id=${parentId}&per_page=1000`)
      .pipe(map(res => res.data))
      .toPromise();
  }

  public getOffers(productId: number, params: GetProductOffersParams = {}): Promise<ProductOffer[]> {
    return this.apiService
      .get(`/products/${productId}/offers`, {
        ...params,
        per_page: 100
      })
      .pipe(map(res => res.data))
      .toPromise();
  }

  public getPlans(parentId: number): Promise<SubscriptionPlan[]> {
    return this.apiService
      .get(`/products/${parentId}/plans?per_page=100`)
      .pipe(map(response => response.data))
      .toPromise();
  }

  public getPlanChangeConfig(planId: number): Promise<PlanChangeConfig> {
    return this.apiService
      .get(`/products/${planId}/planchangeconfig`)
      .pipe(map(response => response.data))
      .toPromise();
  }

  public setPlanChangeConfig({ targetPlanId, ...payload }: SetPlanChangeConfigPayload): Promise<void> {
    return this.apiService
      .post(`/products/${targetPlanId}/planchangeconfig`, {
        ...payload
      })
      .toPromise();
  }

  public clonePlan(planId: number): Promise<SubscriptionPlan> {
    return this.apiService
      .post(`/products/${planId}/clone`, {})
      .pipe(map(response => response.data))
      .toPromise();
  }

  public deletePlan(id: number): Promise<SubscriptionPlan> {
    return this.apiService
      .put(`/products/${id}/changestatus?status=2`, {})
      .pipe(map(response => response.data))
      .toPromise();
  }

  public createOffer(productId: number, offer: CreateOfferPayload): Promise<ProductOffer> {
    return this.apiService
      .post(`/products/${productId}/offers`, offer)
      .toPromise()
      .then(response => response?.data ?? response);
  }

  public updateOffer(productId: number, offerId: number, offer: UpdateOfferPayload): Promise<ProductOffer> {
    return this.apiService
      .put(`/products/${productId}/offers/${offerId}`, offer)
      .toPromise()
      .then(res => res.data);
  }

  public desactivateOffer(productId: number, offerId: number): Promise<void> {
    return this.apiService
      .put(`/products/${productId}/offers/${offerId}`, {
        is_active: 0
      })
      .toPromise();
  }

  public getContentDelivery(productId: number): Promise<IContentDelivery> {
    return this.apiService
      .get(`/products/${productId}/content`)
      .pipe(map(response => response.data))
      .toPromise();
  }

  public loadProductById(id: number, ignoreActiveAffiliates: boolean) {
    let ignoreActiveAffiliatesQuery = '';

    if (ignoreActiveAffiliates) {
      ignoreActiveAffiliatesQuery = '?ignore_active_affiliates=true';
    }

    return apiServiceProxy.get<IProduct>(`/products/${id}${ignoreActiveAffiliatesQuery}`);
  }

  public getCategories(): Promise<any[]> {
    return apiServiceProxy.get('/categories').then(res => res.data);
  }

  public getDeliveryMethods(productId: number): Promise<any[]> {
    return apiServiceProxy.get(`/products/${productId}/deliverymethods`).then(res => res.data);
  }

  public getDeliveryIntegrations(): Promise<any> {
    return apiServiceProxy.get('/shopiary/delivery-integration-status').then(res => res.data);
  }

  public createProduct(product: any): Promise<any> {
    const normalizedProduct = this.normalizeAutomaticPixPaymentMethods(product, true);
    return apiServiceProxy.post('/products', normalizedProduct).then(res => res.data);
  }

  public updateProduct(id: number, product: any): Promise<any> {
    const normalizedProduct = this.normalizeAutomaticPixPaymentMethods(product, false);
    return apiServiceProxy.put(`/products/${id}`, normalizedProduct).then(res => res.data);
  }

  public cloneProduct(id: number, parentId?: number): Promise<any> {
    const query = parentId ? `?parent_id=${parentId}` : '';
    return apiServiceProxy.post(`/products/${id}/clone${query}`, {}).then(res => res.data);
  }

  public enableMelhorEnvioAuth(code: string): Promise<any> {
    return apiServiceProxy.post('/shopiary/integrate/melhor-envio', { melhor_envio_code: code }).then(res => res.data);
  }

  public changeStatus(id: number, status: number): Promise<any> {
    return apiServiceProxy.put(`/products/${id}/changestatus`, { status });
  }

  public inviteAffiliate(id: number, email: string): Promise<any> {
    return apiServiceProxy.post(`/products/${id}/affiliate`, { email });
  }

  public manualDelivery(id: number, customerEmail: string): Promise<any> {
    return apiServiceProxy.post(`/products/${id}/delivery`, { customer_email: customerEmail });
  }

  public getInvoices(id: number): Promise<any> {
    return apiServiceProxy.get(`/products/${id}/invoices`).then(res => res.data);
  }

  public getAffiliatesStatusSummary(id: number): Promise<any> {
    return apiServiceProxy.get(`/affiliates/statussummary/product/${id}`).then(res => res.data);
  }

  public listMyProducts(filters?: any, pagination?: any): Promise<IApiResponse> {
    const pg = {
      per_page: String(pagination?.per_page || 10),
      current_page: String(pagination?.current_page || 1)
    };

    const params = new URLSearchParams({
      per_page: pg.per_page,
      page: pg.current_page,
      filter_fix_delivery: 'false'
    });

    if (filters) {
      if (filters.moderation_status) params.set('moderation_status', filters.moderation_status);
      if (filters.status) params.set('status', filters.status);
      if (filters.order) params.set('orderby', filters.order);

      if (filters.title) {
        if (Number(filters.title)) {
          params.set('id', filters.title);
        } else {
          params.set('title', filters.title);
        }
      }

      if (filters.exact_title) params.set('exact_title', filters.exact_title);
      if (filters.filter_fix_delivery) params.set('filter_fix_delivery', 'true');
    }

    return apiServiceProxy.get(`/products?${params.toString()}`);
  }

  public listCoProductions(pagination: any, title?: string): Promise<IApiResponse> {
    const params = new URLSearchParams({
      per_page: String(pagination.per_page),
      page: String(pagination.current_page)
    });

    if (title) {
      if (Number(title)) {
        params.set('product_id', title);
      } else {
        params.set('title', title);
      }
    }

    return apiServiceProxy.get(`/coproductions?${params.toString()}`);
  }

  public listIrregularProducts(pagination: any, title?: string): Promise<IApiResponse> {
    const params = new URLSearchParams({
      per_page: String(pagination.per_page),
      page: String(pagination.current_page)
    });

    if (title) {
      if (Number(title)) {
        params.set('id', title);
      } else {
        params.set('title', title);
      }
    }

    return apiServiceProxy.get(`/products/irregular?${params.toString()}`);
  }

  public loadFileById(id: number): Promise<IApiResponse> {
    return apiServiceProxy.get(`/files/${id}`);
  }

  public createOrUpdateCampaignTracker(tracker: any, id?: any): Promise<IApiResponse> {
    if (!id) {
      return apiServiceProxy.post('/pixel', tracker);
    }

    return apiServiceProxy.put(`/pixel/${id}`, tracker);
  }

  public loadCampaignTracker(productId: any, type?: any): Promise<IApiResponse> {
    const params = new URLSearchParams({ product_id: String(productId) });
    if (type) params.set('type', String(type));
    return apiServiceProxy.get(`/pixel?${params.toString()}`);
  }

  public verifyProductDomain(productId: number): Promise<IApiResponse> {
    return apiServiceProxy.get(`/pixel/verify?product_id=${productId}`);
  }

  public deleteCampaignTracker(id: any, type: string): Promise<IApiResponse> {
    return apiServiceProxy.delete(`/pixel/${id}?type=${type}`);
  }

  private normalizeAutomaticPixPaymentMethods(product: any, applyNewMainProductDefaults: boolean): any {
    const isLegalPerson = isLegalPersonAccount(userStore.getState().user?.type);
    const normalizedProduct = {
      ...product,
      allows_automatic_pix: isLegalPerson ? product.allows_automatic_pix : false
    };

    if (!applyNewMainProductDefaults || product.parent_id) {
      return normalizedProduct;
    }

    return {
      ...normalizedProduct,
      allows_pix: !isLegalPerson,
      allows_automatic_pix: isLegalPerson,
      allows_combine_payment: !isLegalPerson
    };
  }
}

const productService = new ProductService(apiService);
export default productService;
